export enum BillingRestrictionStage {
	None = 0,
	InitialNotice = 1,
	ViewOnly = 2,
	Locked = 3,
}

export type BillingRestrictionInfo = {
	stage: BillingRestrictionStage
	isAccountScoped: boolean
	isWriteAllowed: boolean
	restrictionStartedAt?: string | null
	finalWarningAt?: string | null
	lockedAt?: string | null
	gracePeriodEndsAt?: string | null
	preLockIsDisabled: boolean
	chargebeeSubscriptionId?: string | null
}

export type BillingCustomerAccess = 'Full' | 'UploadRestricted' | 'Locked'

export type BillingDelinquencyInfo = {
	hasOutstandingBalance: boolean
	chargebeeSubscriptionUrl?: string | null
	detectedAt?: string | null
	lastConfirmedAt?: string | null
	isPendingReview: boolean
	workflowStage: BillingRestrictionStage
	workflowStartedAt?: string | null
	customerAccess: BillingCustomerAccess
	isActivationExcluded: boolean
	isEnforcementEnabled: boolean
	/** False when the Chargebee sync can't flag this account (detection off, or outside the pilot list). */
	isDetectionEnabled?: boolean
	/** False when the account has no account-level Chargebee subscription to pay in 4473 Cloud. */
	isSelfServeBillingEligible?: boolean
	canStartWorkflow: boolean
	startBlockedReason?: string | null
	canStopWorkflow: boolean
}

// Hide the card while detection is off, unless the account still has billing data or a running workflow to manage.
export const shouldShowBillingStatus = (info?: BillingDelinquencyInfo | null) =>
	Boolean(
		info &&
			(info.isDetectionEnabled !== false ||
				info.hasOutstandingBalance ||
				info.canStopWorkflow ||
				info.workflowStage > 0)
	)

export const START_WORKFLOW_CONFIRMATION =
	'Turning on delinquency notifications affects every store on this account. Account owners receive the Initial Notice, and access is restricted on schedule while the workflow stays on. Payment does not turn it off; turn it off here once billing is resolved.'

export const STOP_WORKFLOW_CONFIRMATION =
	'Turning off delinquency notifications stops all billing emails and restores full access for every store on this account, even if a balance is still owed in Chargebee.'

export const isBalancePaidWhileWorkflowActive = (info?: BillingDelinquencyInfo | null) =>
	Boolean(info?.canStopWorkflow && !info?.hasOutstandingBalance)

export const getWorkflowStatusLabel = (info?: BillingDelinquencyInfo | null) => {
	switch (info?.workflowStage) {
		case BillingRestrictionStage.InitialNotice:
			return 'Initial Notice'
		case BillingRestrictionStage.ViewOnly:
			return 'Upload Restricted'
		case BillingRestrictionStage.Locked:
			return 'Locked'
		default:
			return info?.isPendingReview ? 'Flagged' : 'Not started'
	}
}

export const getCustomerAccessLabel = (access?: BillingCustomerAccess | null) => {
	switch (access) {
		case 'UploadRestricted':
			return 'Uploads restricted'
		case 'Locked':
			return 'Locked'
		default:
			return 'Full access'
	}
}

export const getStartWorkflowErrorMessage = (error: unknown) => {
	const response = (error as { response?: { status?: number; data?: unknown } })?.response
	if (typeof response?.data === 'string' && response.data.trim()) return response.data
	if (response?.status === 502)
		return 'Chargebee could not confirm the balance. Try again shortly.'
	return 'Unable to start the delinquency workflow.'
}

export const getStopWorkflowErrorMessage = (error: unknown) => {
	const response = (error as { response?: { status?: number; data?: unknown } })?.response
	if (typeof response?.data === 'string' && response.data.trim()) return response.data
	return 'Unable to turn off the delinquency workflow.'
}

export const getBillingRestrictionStageLabel = (
	restriction?: BillingRestrictionInfo | null,
) => {
	switch (restriction?.stage) {
		case BillingRestrictionStage.InitialNotice:
			return 'Initial Notice'
		case BillingRestrictionStage.ViewOnly:
			return 'Upload Restricted'
		case BillingRestrictionStage.Locked:
			return 'Locked'
		default:
			return null
	}
}
